/**
 * Social-Asset-Hub — die Formen, die vom Backend kommen (CUR-323).
 *
 * Handgeschrieben und nicht generiert, wie im ganzen Dashboard. Sie muessen zu
 * `curalearn-backend/src/routes/admin/social/social.service.ts` passen; wer dort
 * ein Feld ergaenzt, ergaenzt es hier.
 */

/** Kanäle, auf die veröffentlicht wird — Kalender, Rhythmen, Termine (CUR-2247). */
export const PUBLISH_CHANNELS = ['YOUTUBE', 'INSTAGRAM', 'TIKTOK', 'FACEBOOK'] as const;
export type Channel = (typeof PUBLISH_CHANNELS)[number];

/**
 * Ziele eines Review-Entwurfs. `APP` ist die Fachprüfung einer App-Fassung —
 * sie wird nie veröffentlicht und steht deshalb nicht im Kalender.
 */
export const REVIEW_TARGETS = [...PUBLISH_CHANNELS, 'APP'] as const;
export type ReviewTarget = (typeof REVIEW_TARGETS)[number];

export const POST_STATUS = ['GEPLANT', 'BEREIT', 'GEPOSTET', 'VERSCHOBEN', 'VERWORFEN'] as const;
export type PostStatus = (typeof POST_STATUS)[number];

/**
 * Ordner im Repo und sein Kürzel (CUR-2287). Das Kürzel leitet der Server ab
 * (`ordnerKuerzel()`), der Client rechnet es nie nach — sonst nennten Zeile und
 * Suche irgendwann zwei verschiedene Kürzel für denselben Ordner.
 */
export interface OrdnerAngabe {
  /** `E31`, `S03`, `LC-01` — oder die Kennung (`tour`); `null` bei Entwürfen ohne Ordner. */
  kuerzel: string | null;
  /** Repo-relativ, z. B. `docs/marketing/video/erklaervideos-lektionen/E31-hormonsystem-diabetes`. */
  ordner: string | null;
}

export interface AssetKurz extends OrdnerAngabe {
  id: string;
  slug: string;
  stream: string;
  title: string;
  aspectRatio: string | null;
  durationSec: number | null;
  thumbnailUrl: string | null;
}

/** Christines Freigabe, abgeleitet im Backend (`social.lage.ts`) — nie im Client nachgerechnet. */
export type ReviewLageState =
  | 'OHNE_REVIEW'
  | 'ENTWURF'
  | 'WARTET'
  | 'FREIGEGEBEN'
  | 'AENDERUNG_ERBETEN'
  | 'FREIGABE_AUFGEHOBEN'
  | 'ZURUECKGEZOGEN';

/** D25 (CUR-2291): Stand der Langfassung derselben Folge — ein Hinweis, keine Sperre. */
export interface LangfassungsStand {
  state: 'FREIGEGEBEN' | 'OFFEN' | 'FEHLT' | 'OHNE_CHRISTINE';
  /** Christines Anmerkung zu einem 👍 — betrifft sie die Kürzung? */
  anmerkung: string | null;
}

export interface ReviewLage {
  state: ReviewLageState;
  number: number | null;
  /** Erste Zeile von Christines Anmerkung. */
  comment: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  mailFehler: boolean;
  /**
   * Christines Urteil kommt von der Fassung dieses Kanals: Sie zeigt dasselbe Video (K4, Spec §17
   * V2). `null` bei der eigenen Einreichung; fehlt bei Antworten vor K4.
   */
  geerbtVon?: ReviewTarget | null;
  /** Grund, warum Christine diese Fassung nicht prüft (K2) — dann heißt sie „Ohne Christine". */
  verzicht: string | null;
  /** Stand der Langfassung derselben Folge, nur bei einer Fassung mit Verzicht. */
  langfassung: LangfassungsStand | null;
}

export type PublicationState = 'WARTET' | 'OFFEN' | 'LIVE' | 'VERSCHOBEN' | 'VERWORFEN';

export interface PublicationLage {
  state: PublicationState;
  url: string | null;
  /** Wörtlich aus `approvalBlockReason()` — Zeile und Server nennen denselben Grund. */
  blockReason: string | null;
}

export interface Termin {
  id: string;
  /** Kalendertag `YYYY-MM-DD` in Redaktionszeit — kein Zeitpunkt. `null` = ohne Termin. */
  date: string | null;
  channel: ReviewTarget;
  status: PostStatus;
  /**
   * Abgeleitet (Datum < heute und Status != GEPOSTET), nicht gespeichert.
   * Deshalb kommt der Wert vom Server: Ein Client, der ihn selbst rechnet,
   * rechnet ihn in SEINER Zeitzone.
   */
  overdue: boolean;
  postedUrl: string | null;
  review: ReviewLage;
  publication: PublicationLage;
  asset: AssetKurz;
}

export interface FreierSlot {
  ruleId: string;
  date: string;
  stream: string;
  channel: Channel;
}

/**
 * Drei Faelle, drei Werte. „Vorrat leer" und „reicht weiter als der Horizont"
 * duerfen sich keinen Wert teilen — der erste ist der knappste Fall, der zweite
 * der entspannteste.
 */
export type Reichweite =
  | { art: 'leer' }
  | { art: 'bis'; datum: string }
  | { art: 'ueber-horizont' };

export interface Rhythmus {
  id: string;
  stream: string;
  channel: Channel;
  weekdays: number[];
  startsOn: string;
  endsOn: string | null;
  skippedDates: string[];
  active: boolean;
  vorrat: number;
  reichweite: Reichweite;
  knapp: boolean;
}

export interface KalenderAntwort {
  termine: Termin[];
  freieSlots: FreierSlot[];
  rhythmen: Rhythmus[];
  /** Entwürfe ohne Termin — stehen über der Agenda, nie als überfällig. */
  ohneTermin: Termin[];
  /** App-Fassungen zur Fachprüfung; freigegebene verschwinden nach zwei Wochen. */
  fachreview: Termin[];
  /** Was bei Christine wartet, über alle Monate. */
  lagen: { wartetSocial: number; wartetApp: number };
  /**
   * Die Suche, nach der der Server gefiltert hat (CUR-2287) — über alle Monate,
   * ohne freie Slots. `KUERZEL` heißt Gleichheit (`e31` findet E31, nicht E310),
   * `TITEL` „Titel enthält". `null` = keine Suche.
   */
  suche: {
    q: string;
    art: 'KUERZEL' | 'TITEL';
    /** Die Obergrenze war erreicht — gezeigt werden die neuesten Treffer. */
    gekuerzt?: boolean;
  } | null;
}

export interface Pruefpunkt {
  id: string;
  text: string;
  blocking?: boolean;
}

export interface Kanalfassung {
  /** Seit wann der Kanal nicht mehr im Manifest steht (CUR-2288); `null` = er steht drin. */
  nichtImManifestSeit: string | null;
  title: string | null;
  description: string | null;
  tags: string[];
  hashtags: string[];
  altText: string | null;
  chapters: string | null;
  /**
   * Geordnete Liste, kein Objekt. Postgres speichert `Json` als jsonb und
   * sortiert Objektschlüssel um — die Reihenfolge der Einstellungen ist hier
   * aber Aussage: Sie bildet die Upload-Maske von oben nach unten ab.
   */
  settings: { label: string; value: string }[] | null;
  checklist: Pruefpunkt[] | null;
  notes: Record<string, string> | null;
}

export interface CockpitDaten {
  id: string;
  channel: ReviewTarget;
  date: string | null;
  status: PostStatus;
  overdue: boolean;
  postedAt: string | null;
  postedUrl: string | null;
  checkedItems: string[];
  note: string | null;
  /** Titel dieser Plattform-Fassung — die einzige Titelquelle (CUR-2249). */
  title: string | null;
  caption: string | null;
  /** YouTube-Tags dieser Fassung (CUR-2295), Teil der Freigabe. Leer bei anderen Kanälen. */
  tags: string[];
  reviewRequired: boolean;
  /** Grund, warum Christine diese Fassung nicht prüft (K2, CUR-2291). */
  reviewVerzicht: string | null;
  /** Stand der Langfassung derselben Folge (D25) — nur mit Verzicht. */
  langfassung: LangfassungsStand | null;
  media: ReviewMedia[];
  review: ReviewRevision | null;
  reviewHistory: ReviewRevision[];
  reviewBlockReason: string | null;
  /** Stand bei Christine wie im Kalender — auch übernommen von einer Fassung mit demselben Video (K4). */
  freigabe: ReviewLage;
  /** Christines Anmerkung zu diesem Urteil, vollständig (die Lage kürzt auf eine Zeile). */
  freigabeAnmerkung?: string | null;
  /** Die anderen Plattform-Fassungen desselben Beitrags — Quelle für „Medien übernehmen". */
  geschwister: { id: string; channel: ReviewTarget; status: PostStatus; mediaCount: number; mediaComplete: boolean }[];
  asset: OrdnerAngabe & {
    id: string;
    slug: string;
    /** Aus dem Import: Der Ordner folgt dem Manifest und ist im Cockpit nicht änderbar (§16 O3, CUR-2289). */
    ausManifest: boolean;
    stream: string;
    title: string;
    videoPath: string | null;
    videoUrl: string | null;
    thumbnailUrl: string | null;
    durationSec: number | null;
    aspectRatio: string | null;
    fileSizeMb: number | null;
    /** Kennzeichnung für alle Fassungen; `null` = noch nicht entschieden. */
    aiLabel: boolean | null;
    advertising: boolean | null;
  };
  /** `null`, wenn fuer diesen Kanal keine Fassung existiert — der Termin ist dann nicht postbar. */
  variant: Kanalfassung | null;
}

export interface ReviewMedia {
  id: string;
  kind: 'VIDEO' | 'IMAGE' | 'COVER';
  position: number;
  mimeType: string;
  bytes: number;
  complete: boolean;
}

export interface ReviewRevision {
  id: string;
  number: number;
  state: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';
  comment: string | null;
  submittedAt: string;
  decidedAt: string | null;
  withdrawnAt: string | null;
  notificationSentAt: string | null;
  notificationError: string | null;
}

export interface ReviewRevisionDetail {
  id: string;
  number: number;
  state: ReviewRevision['state'];
  /** Eingereichter Titel; `null` bei Fassungen vor CUR-2249 oder ohne Titel. */
  title: string | null;
  caption: string;
  /** Eingereichte YouTube-Tags (CUR-2295); leer bei anderen Kanälen und älteren Fassungen. Fehlt bei
   *  einem Backend vor diesem Stand — Admin und Backend werden nicht im selben Augenblick ausgeliefert. */
  tags?: string[];
  comment: string | null;
  submittedAt: string;
  decidedAt: string | null;
  media: { id: string; kind: ReviewMedia['kind']; position: number; mimeType: string; checksum: string | null; url: string }[];
}

export interface BestandsAsset extends OrdnerAngabe {
  id: string;
  slug: string;
  stream: string;
  title: string;
  durationSec: number | null;
  aspectRatio: string | null;
  fileSizeMb: number | null;
  thumbnailUrl: string | null;
  videoPath: string | null;
  /** Wohin es ueberhaupt gehen kann (welche Fassungen es gibt). */
  channels: ReviewTarget[];
  /** Wo es schon steht. Leer = unverplant. */
  scheduledOn: { channel: ReviewTarget; status: PostStatus; date: string | null }[];
}
